import React from "react"
import { useSelector } from "react-redux"
import { Box, makeStyles } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle } from "@fortawesome/pro-light-svg-icons/faCheckCircle"
import { lightBlue } from "@material-ui/core/colors"
import { StandardButton } from "shared/Components/Button/StandardButton"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { selectUserLocationName } from "shared/Modules/User/userSelectors"
import { OnboardingDrawer } from "./OnboardingDrawer"
import { ONBOARDING_STEP_COUNT, PushPermissionResult } from "../onboardingTypes"

const useStyles = makeStyles((theme) => ({
    iconBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: theme.spacing(8),
        height: theme.spacing(8),
        borderRadius: theme.spacing(2),
        backgroundColor: lightBlue[50],
        color: theme.palette.primary.main,
        fontSize: theme.spacing(3.5),
        marginBottom: theme.spacing(3),
    },
}))

type Props = Readonly<{
    pushResult: PushPermissionResult | undefined
    onDone: () => void
}>

function renderPushSummary(pushResult: PushPermissionResult | undefined) {
    const granted = pushResult?.granted === true
    const forTx = granted && pushResult.forTx
    const forNews = granted && pushResult.forNews

    if (forTx && forNews) {
        return (
            <LocalizedStrict id="onboarding-done-push-both">
                <span>Du får beskeder om drift og ordrer samt nyheder og tilbud. Ændres under Mig › Indstillinger › Pushbeskeder.</span>
            </LocalizedStrict>
        )
    }
    if (forTx) {
        return (
            <LocalizedStrict id="onboarding-done-push-tx">
                <span>Du får beskeder om drift og ordrer. Ændres under Mig › Indstillinger › Pushbeskeder.</span>
            </LocalizedStrict>
        )
    }
    if (forNews) {
        return (
            <LocalizedStrict id="onboarding-done-push-news">
                <span>Du får beskeder om nyheder og tilbud. Ændres under Mig › Indstillinger › Pushbeskeder.</span>
            </LocalizedStrict>
        )
    }
    return (
        <LocalizedStrict id="onboarding-done-push-none">
            <span>Du får ingen pushbeskeder. Du kan slå dem til under Mig › Indstillinger › Pushbeskeder.</span>
        </LocalizedStrict>
    )
}

/**
 * Final screen of the onboarding flow: confirms the chosen location and summarizes the push choice.
 */
export function OnboardingDoneStep({ pushResult, onDone }: Props) {
    const classes = useStyles()
    const locationName = useSelector(selectUserLocationName)

    return (
        <OnboardingDrawer
            name="onboarding-done"
            totalSteps={ONBOARDING_STEP_COUNT}
            media={
                <Box className={classes.iconBox}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                </Box>
            }
            title={
                locationName
                    ? <LocalizedStrict id="onboarding-done-title" vars={{ location: locationName }}><span>{locationName} er valgt</span></LocalizedStrict>
                    : <LocalizedStrict id="onboarding-done-title-no-location"><span>Du er klar</span></LocalizedStrict>
            }
            description={renderPushSummary(pushResult)}
            actions={
                <LocalizedStrict id="onboarding-done-button">
                    <StandardButton variant="contained" color="black" size="large" fullWidth onClick={onDone}>
                        Til forsiden
                    </StandardButton>
                </LocalizedStrict>
            }
        />
    )
}
